import React, { useState, useEffect } from 'react';
import {
  TextField,
  Button,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Grid,
  Paper,
  Divider,
  Link,
  useTheme,
  FormHelperText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  InputAdornment
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import api from '../../utils/api';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Link as RouterLink } from 'react-router-dom';
import useComponentStyles from '../../theme/useComponentStyles';

function Register() {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
  });
  
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [generalError, setGeneralError] = useState('');
  const [verificationStep, setVerificationStep] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [userId, setUserId] = useState(null);
  const [referrerName, setReferrerName] = useState(null);
  const [loadingReferral, setLoadingReferral] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const _theme = useTheme();
  const _styles = useComponentStyles();
  const { _login, reinitializeAuth } = useAuth();
  const inviteToken = new URLSearchParams(location.search).get('invite');
  const referralCode = new URLSearchParams(location.search).get('ref');

  // Fetch referral config when referral code is present
  useEffect(() => {
    const fetchReferralConfig = async () => {
      if (!referralCode) {
        setReferrerName(null);
        return;
      }

      try {
        setLoadingReferral(true);
        const response = await api.get(`/api/referrals/config/${referralCode}`);
        if (response.data && response.data.referrerName) {
          setReferrerName(response.data.referrerName);
        }
      } catch (error) {
        // Silently fail - referral code might not exist, that's okay
        setReferrerName(null);
      } finally {
        setLoadingReferral(false);
      }
    };

    fetchReferralConfig();
  }, [referralCode]);

  // Password requirements
  const passwordRequirements = {
    minLength: 8,
  };

  // Validation functions
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email) ? '' : 'Please enter a valid email address';
  };

  const validatePassword = (password) => {
    if (!password) return 'Password is required';
    if (password.length < passwordRequirements.minLength) {
      return `Password must be at least ${passwordRequirements.minLength} characters long`;
    }
    return '';
  };

  const validateName = (name, fieldName) => {
    if (!name.trim()) return `${fieldName} is required`;
    if (name.trim().length < 2) return `${fieldName} must be at least 2 characters long`;
    const nameRegex = /^[A-Za-z\s-']+$/;
    if (!nameRegex.test(name.trim())) {
      return `${fieldName} can only contain letters, spaces, hyphens, and apostrophes`;
    }
    return '';
  };

  const validateConfirmPassword = (confirmPassword, password) => {
    if (!confirmPassword) return 'Please confirm your password';
    if (confirmPassword !== password) return 'Passwords do not match';
    return '';
  };

  // Handle field change
  const handleChange = (field) => (event) => {
    const value = event.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear general error when user starts typing
    if (generalError) setGeneralError('');
    
    // Validate the field
    let fieldError = '';
    switch (field) {
      case 'email':
        fieldError = validateEmail(value);
        break;
      case 'password':
        fieldError = validatePassword(value);
        // Also validate confirm password if it exists
        if (formData.confirmPassword) {
          setErrors(prev => ({
            ...prev,
            confirmPassword: validateConfirmPassword(formData.confirmPassword, value)
          }));
        }
        break;
      case 'confirmPassword':
        fieldError = validateConfirmPassword(value, formData.password);
        break;
      case 'firstName':
        fieldError = validateName(value, 'First name');
        break;
      case 'lastName':
        fieldError = validateName(value, 'Last name');
        break;
      default:
        break;
    }

    setErrors(prev => ({
      ...prev,
      [field]: fieldError
    }));
  };

  // Validate all fields
  const validateForm = () => {
    const newErrors = {
      email: validateEmail(formData.email),
      password: validatePassword(formData.password),
      confirmPassword: validateConfirmPassword(formData.confirmPassword, formData.password),
      firstName: validateName(formData.firstName, 'First name'),
      lastName: validateName(formData.lastName, 'Last name')
    };
    
    setErrors(newErrors);
    
    // Return true if no errors (all values are empty strings)
    return Object.values(newErrors).every(error => error === '');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Prevent multiple submissions
    if (loading) return;
    
    // Validate form
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      setGeneralError('');
      
      const response = await api.post('/api/auth/register', {
        email: formData.email.trim(),
        password: formData.password,
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        inviteToken,
        referralCode: referralCode || null
      });
      
      // Set user ID and show verification dialog
      setUserId(response.data.userId);
      setVerificationStep(true);
      
    } catch (error) {
      // Handle validation errors from the server
      if (error.response?.data?.errors) {
        const serverErrors = {};
        error.response.data.errors.forEach(err => {
          if (err.field) {
            serverErrors[err.field] = err.message;
          } else {
            // If no field specified, set as general error
            setGeneralError(err.message || 'Validation failed');
          }
        });
        
        if (Object.keys(serverErrors).length > 0) {
          setErrors(prev => ({ ...prev, ...serverErrors }));
        }
      } else {
        setGeneralError(
          error.response?.data?.message || 
          error.message || 
          'Registration failed. Please try again.'
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerificationSubmit = async () => {
    if (!verificationCode.trim()) {
      setGeneralError('Please enter the verification code');
      return;
    }

    try {
      setLoading(true);
      setGeneralError('');

      // Try to connect directly to the backend service
      try {
        // Create a proper URL with the current domain
        const currentDomain = window.location.hostname;
        const _backendUrl = `http://${currentDomain}/api/auth/verify-email`;

        // Make sure the code is always sent as a string
        const verificationCodeStr = String(verificationCode.trim());

        // Use the existing api instance instead of trying to require axios
        const response = await api.post('/api/auth/verify-email', {
          userId,
          code: verificationCodeStr  // Send as string
        });

        const data = response.data;

        // Store authentication data
        localStorage.setItem('token', data.token);
        localStorage.setItem('userId', data.user.id);
        localStorage.setItem('userType', 'user');
        localStorage.setItem('userEmail', data.user.email);
        localStorage.setItem('tokenSetTime', Date.now().toString()); // Track when token was set

        // Set token in API headers
        api.defaults.headers.common['x-auth-token'] = data.token;

        // Clear any previous errors
        setGeneralError('');

        // Close the verification dialog
        setVerificationStep(false);
        setVerificationCode('');

        try {
          await reinitializeAuth();
        } catch (authError) {}

        // Use setTimeout to ensure auth initialization completes before navigation
        setTimeout(() => {
          navigate('/no-access', { replace: true });
        }, 500);
      } catch (error) {
        setGeneralError(`Verification failed: ${error.message || 'Unknown error'}`);
      }
    } catch (error) {
      setGeneralError(
        error.response?.data?.message ||
        error.message ||
        'Verification failed. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    try {
      setLoading(true);
      setGeneralError('');

      const response = await api.post('/api/auth/resend-verification', {
        userId,
        isLogin: false
      });

      if (response.data.emailSent !== false) {
        setGeneralError('A new verification code has been sent to your email');
      } else {
        setGeneralError('Failed to send verification email. Please try again or contact support.');
      }
    } catch (error) {
      setGeneralError(
        error.response?.data?.message ||
        error.message ||
        'Failed to resend verification code'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ 
      minHeight: '100vh',
      backgroundColor: 'secondary.main', // Lavender background
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      py: 4
    }}>
      {/* Logo */}
      <Box 
        sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          mb: 4
        }}
      >
        <img src={`${process.env.PUBLIC_URL}/img/logo.svg`} alt="Idotoo Logo" style={{ height: '40px' }} />
      </Box>

      {/* Register Card */}
      <Paper 
        elevation={0}
        sx={{ 
          backgroundColor: 'background.paper',
          maxWidth: '400px',
          width: '100%',
          mx: 2,
          overflow: 'hidden',
          border: '1px solid rgba(0, 0, 0, 0.08)'
        }}
      >
        <Box sx={{ p: 4 }}>
          <Typography 
            variant="h5" 
            component="h2" 
            align="center" 
            gutterBottom
            sx={{ 
              fontWeight: 500,
              color: 'text.primary',
              mb: 3
            }}
          >
            Create Account
          </Typography>
              
          {generalError && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {generalError}
            </Alert>
          )}

          {referralCode && (loadingReferral || referrerName) && (
            <Alert 
              icon={false}
              sx={{ 
                mb: 3,
                backgroundColor: '#fff3f8',
                border: '1px solid #ffb3d9',
                borderRadius: 2,
                '& .MuiAlert-message': {
                  width: '100%',
                  textAlign: 'center'
                }
              }}
            >
              {loadingReferral ? (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                  <CircularProgress size={16} />
                  <Typography variant="body2">Loading...</Typography>
                </Box>
              ) : referrerName ? (
                <Typography variant="body2" sx={{ textAlign: 'center' }}>
                  💕 The Amazing <strong style={{ color: '#e91e63' }}>{referrerName}</strong> has invited you to become part of the idotoo community! 💕
                </Typography>
              ) : null}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="First Name"
                  value={formData.firstName}
                  onChange={handleChange('firstName')}
                  required
                  variant="outlined"
                  autoComplete="given-name"
                  error={!!errors.firstName}
                  helperText={errors.firstName}
                  size="small"
                  sx={{ 
                    mb: 2,
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#fff'
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Last Name"
                  value={formData.lastName}
                  onChange={handleChange('lastName')}
                  required
                  variant="outlined"
                  autoComplete="family-name"
                  error={!!errors.lastName}
                  helperText={errors.lastName}
                  size="small"
                  sx={{ 
                    mb: 2,
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#fff'
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange('email')}
                  required
                  variant="outlined"
                  autoComplete="email"
                  error={!!errors.email}
                  helperText={errors.email}
                  size="small"
                  sx={{ 
                    mb: 2,
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#fff'
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={handleChange('password')}
                  required
                  variant="outlined"
                  autoComplete="new-password"
                  error={!!errors.password}
                  helperText={errors.password}
                  size="small"
                  sx={{ 
                    mb: 2,
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#fff'
                    }
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                          size="small"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <FormHelperText>
                  Password must be at least {passwordRequirements.minLength} characters long
                </FormHelperText>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Confirm Password"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={formData.confirmPassword}
                  onChange={handleChange('confirmPassword')}
                  required
                  variant="outlined"
                  autoComplete="new-password"
                  error={!!errors.confirmPassword}
                  helperText={errors.confirmPassword}
                  size="small"
                  sx={{ 
                    mb: 3,
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#fff'
                    }
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle confirm password visibility"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          edge="end"
                          size="small"
                        >
                          {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>
            
            <Button
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
              sx={{ 
                py: 1.5,
                mt: 3, 
                mb: 2,
                textTransform: 'none',
                fontSize: '1rem',
                fontWeight: 500,
                color: '#ffffff',
                border: '1px solid #000'
              }}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : 'Create Account'}
            </Button>
            
            <Divider sx={{ my: 2 }}>
              <Typography variant="body2" color="text.secondary">
                OR
              </Typography>
            </Divider>
            
            <Box sx={{ textAlign: 'center' }}>
              <Link component={RouterLink} to="/login" underline="hover" sx={{ color: 'black' }}>
                Already have an account? Sign in
              </Link>
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* Email Verification Dialog */}
      <Dialog open={verificationStep} maxWidth="xs" fullWidth>
        <DialogTitle>Verify Your Email</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Typography gutterBottom>
              Please enter the verification code sent to your email address.
            </Typography>
            <TextField
              fullWidth
              label="Verification Code"
              value={verificationCode}
              onChange={(e) => {
                // Only allow digits and limit to 6 characters
                const value = e.target.value.replace(/[^0-9]/g, '').slice(0, 6);
                setVerificationCode(value);
              }}
              margin="normal"
              variant="outlined"
              placeholder="Enter 6-digit code"
              size="small"
              inputProps={{
                maxLength: 6,
                inputMode: 'numeric',
                pattern: '[0-9]*'
              }}
              sx={{ 
                mb: 3,
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#fff',
                  letterSpacing: 2
                }
              }}
            />
            {generalError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {generalError}
              </Alert>
            )}
            <Button
              variant="contained"
              onClick={handleResendCode}
              disabled={loading}
              size="small"
              sx={{
                mt: 1,
                textTransform: 'none',
                fontSize: '0.875rem',
                fontWeight: 500,
                backgroundColor: '#C2185B',
                color: '#ffffff',
                '&:hover': {
                  backgroundColor: '#AD1457',
                },
                '&:active': {
                  backgroundColor: '#880E4F',
                },
                '&:disabled': {
                  backgroundColor: '#e0e0e0',
                  color: '#9e9e9e',
                }
              }}
            >
              Resend Code
            </Button>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            variant="contained"
            onClick={handleVerificationSubmit}
            disabled={loading || !verificationCode.trim()}
            fullWidth
            sx={{
              py: 1.5,
              textTransform: 'none',
              fontSize: '1rem',
              fontWeight: 500,
              backgroundColor: '#000000',
              color: '#ffffff',
              '&:hover': {
                backgroundColor: '#333333',
              },
              '&:active': {
                backgroundColor: '#1a1a1a',
              },
              '&:disabled': {
                backgroundColor: '#e0e0e0',
                color: '#9e9e9e',
                border: 'none'
              }
            }}
          >
            {loading ? <CircularProgress size={24} /> : 'Verify'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Register; 